import { AfterRenderRef, Component, Injector, OnDestroy, OnInit, Signal, ViewChild, afterNextRender, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { BmtSearchComponent } from '@roosevelt/client-ui-lib';
import { INotices } from 'src/app/store/reports/state';
import moment from 'moment';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { createFeatureManagedClientSearchController, provideClientSearchStatePrefix } from '../../shared/client-search-render.controller';
import { MONTHS } from '../../utils/constants';
import { startWith } from 'rxjs/operators';

type OverageReportType = 'Notice' | 'Adhoc Report' | 'Term Consolidated Report';
const OVERAGE_DEPENDENT_CLIENT_SEARCH_PREFIX = 'reports-overage-dependent-';
const OVERAGE_AS_OF_DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY'];

export interface OverageDependentFormValue {
  planAcronym: string;
  clientSpecifiedId: string;
  subclientSpecifiedId: string;
  asOf: string;
  startOfAgeRange: string;
  uptoAgeRange: string;
  month: string;
  year: number;
}

@Component({
    selector: 'app-overage-dependent-page',
    templateUrl: './overage-dependent.component.html',
    styleUrls: ['./overage-dependent.component.scss'],
    providers: [provideClientSearchStatePrefix(OVERAGE_DEPENDENT_CLIENT_SEARCH_PREFIX)],
    standalone: false
})
export class OverageDependentComponent implements OnInit, OnDestroy {
  // which report are we showing
  readonly reportType = input<OverageReportType>('Notice');
  readonly selectedAdminPlan = input('');
  private readonly selectedPlanAcronym = signal('');
  private readonly clientSearchResetVersion = signal(0);
  allReportForm: UntypedFormGroup;
  readonly isFetching = input(false);
  readonly errors = input('');
  readonly notices = input<INotices[]>([]);
  readonly adhocReportUrl = input<string | undefined>(undefined);
  readonly consolidatedReportUrl = input<string | undefined>(undefined);
  readonly isFormSubmitted = signal(false);

  processFormData = output<OverageDependentFormValue>();
  resetFormData = output<boolean>();
  getODNoticeDoc = output<INotices>();
  readonly searchFormWidthCssClass = computed(() => {
    switch (this.reportType()) {
      case 'Adhoc Report':
        return 'col-md-9';
      case 'Term Consolidated Report':
        return 'col-md-3';
      default:
        return 'col-md-6';
    }
  });

  private clientSearchAsOfSyncRef: AfterRenderRef | null = null;
  private clientSearchComponentInstance?: BmtSearchComponent;
  yearsOptions: number[] = [];
  monthOptions = MONTHS;
  private readonly injector = inject(Injector);
  private readonly lux = inject(Lux<any>);
  private readonly allReportFormValue: Signal<Record<string, unknown>>;
  private initialized = false;

  @ViewChild('overageClientSearch')
  private set clientSearchComponent(component: BmtSearchComponent | undefined) {
    this.clientSearchComponentInstance = component;
    if (component && this.reportType() === 'Adhoc Report') {
      this.scheduleClientSearchAsOfSync();
    }
  }

  readonly clientSearchMountKey = computed(() => {
    return `${this.selectedAdminPlan()}|${this.reportType()}|${this.initialAsOfDate}|${this.clientSearchResetVersion()}`;
  });
  readonly clientSearch = createFeatureManagedClientSearchController({
    lux: this.lux,
    storagePrefix: OVERAGE_DEPENDENT_CLIENT_SEARCH_PREFIX,
    selectedAdminPlan: this.selectedAdminPlan,
    remountTrigger: this.clientSearchMountKey,
    isActive: () => this.initialized
  });

  get initialAsOfDate(): string {
    return moment().add(1, 'days').format('MM/DD/YYYY');
  }

  get clientSearchInitialAsOfDate(): string {
    return moment().add(1, 'days').format('YYYY-MM-DD[T]00:00:00');
  }

  get clientSearchAsOfDateValue(): string {
    return moment().add(1, 'days').format('YYYY-MM-DD');
  }

  get clientSearchAsOfDateDisplayValue(): string {
    return moment(this.clientSearchAsOfDateValue, 'YYYY-MM-DD', true).format('MM/DD/YYYY');
  }

  constructor(private readonly fb: UntypedFormBuilder) {
    this.allReportForm = this.createForm();
    this.allReportFormValue = toSignal(
      this.allReportForm.valueChanges.pipe(startWith(this.allReportForm.getRawValue() as Record<string, unknown>)),
      {
        initialValue: this.allReportForm.getRawValue() as Record<string, unknown>,
        injector: this.injector
      }
    );

    effect(() => {
      this.reportType();
      this.setFormState();
    });

    effect(() => {
      const shouldSyncAdhocAsOfDate = this.reportType() === 'Adhoc Report'
        && !!this.selectedAdminPlan()
        && this.clientSearch.renderClientSearch();

      if (!shouldSyncAdhocAsOfDate) {
        return;
      }

      this.allReportForm.controls['asOf'].setValue(this.initialAsOfDate);
      this.scheduleClientSearchAsOfSync();
    });

    effect(() => {
      this.allReportFormValue();
      this.isFormSubmitted.set(false);
    }, { injector: this.injector });
  }

  ngOnInit(): void {
    this.initialized = true;
    this.clientSearch.scheduleRender(true);
  }

  ngOnDestroy(): void {
    this.clearScheduledClientSearchAsOfSync();
    this.clientSearch.destroy();
  }

  createForm(): UntypedFormGroup {
    const year = moment().year();
    for (let i = 0; i <= 5; i++) {
      this.yearsOptions.push(year - i);
    }

    return this.fb.group({
      clientSpecifiedId: ['', Validators.required],
      subclientSpecifiedId: [{ value: '', disabled: this.reportType() === 'Term Consolidated Report' }, Validators.required],
      asOf: [{ value: this.initialAsOfDate, disabled: this.reportType() !== 'Adhoc Report' }, Validators.required],
      startOfAgeRange: [{ value: '', disabled: this.reportType() !== 'Adhoc Report' }, Validators.required],
      uptoAgeRange: [{ value: '', disabled: this.reportType() !== 'Adhoc Report' }, Validators.required],
      month: [{ value: '', disabled: this.reportType() !== 'Term Consolidated Report' }, Validators.required],
      year: [{ value: '', disabled: this.reportType() !== 'Term Consolidated Report' }, Validators.required]
    }, { validators: this.reportsValidations() });
  }

  reportsValidations() {
    return (form: AbstractControl): ValidationErrors | null => {
      const asOf = form.get('asOf');
      if (asOf?.valid) {
        const parsedAsOf = moment(asOf.value, OVERAGE_AS_OF_DATE_FORMATS, true);
        if (parsedAsOf.isValid() && parsedAsOf.isBefore(moment())) {
          asOf.setErrors({ mindate: moment(new Date()).format('MM/DD/YYYY') });
        } else {
          asOf.setErrors(null);
        }
      }

      return null;
    };
  }

  setFormState(): void {
    switch (this.reportType()) {
      case 'Adhoc Report':
        ['asOf', 'startOfAgeRange', 'uptoAgeRange', 'subclientSpecifiedId'].forEach(key => this.allReportForm.controls[key].enable());
        ['month', 'year'].forEach(key => this.allReportForm.controls[key].disable());
        break;
      case 'Notice':
        ['asOf', 'startOfAgeRange', 'uptoAgeRange', 'month', 'year'].forEach(key => this.allReportForm.controls[key].disable());
        ['subclientSpecifiedId'].forEach(key => this.allReportForm.controls[key].enable());
        break;
      case 'Term Consolidated Report':
        ['asOf', 'startOfAgeRange', 'uptoAgeRange', 'subclientSpecifiedId'].forEach(key => this.allReportForm.controls[key].disable());
        ['month', 'year'].forEach(key => this.allReportForm.controls[key].enable());
        break;
      default:
        break;
    }

    this.resetReportForm();
  }

  resetReportForm(): void {
    this.isFormSubmitted.set(false);
    this.selectedPlanAcronym.set('');
    this.allReportForm.setValue({
      clientSpecifiedId: '',
      subclientSpecifiedId: '',
      asOf: this.initialAsOfDate,
      startOfAgeRange: '19',
      uptoAgeRange: '99',
      month: moment().format('MM'),
      year: this.yearsOptions[0]
    });
    this.clientSearchResetVersion.update(version => version + 1);
  }

  resetField(event: string): void {
    if (event === 'groupInfo') {
      this.selectedPlanAcronym.set('');
      this.allReportForm.controls['clientSpecifiedId'].setValue('');
    }

    this.allReportForm.controls['subclientSpecifiedId'].setValue('');
  }

  setClientId(clientInfo: { specifiedId?: string; planAcronym?: string | string[] } | null | undefined): void {
    this.selectedPlanAcronym.set(this.normalizePlanAcronym(clientInfo?.planAcronym));
    this.allReportForm.controls['clientSpecifiedId'].setValue(clientInfo?.specifiedId ?? '');
    this.allReportForm.controls['subclientSpecifiedId'].setValue('');
  }

  getSubClientDetails(
    clientInfo: Array<{
      client?: { specifiedId?: string; planAcronym?: string | string[] };
      planAcronym?: string | string[];
      specifiedId?: string;
      asOfDate?: string;
    }> | null | undefined
  ): void {
    if (!clientInfo?.length) {
      return;
    }

    this.selectedPlanAcronym.set(this.normalizePlanAcronym(clientInfo[0]?.planAcronym ?? clientInfo[0]?.client?.planAcronym));
    this.allReportForm.controls['clientSpecifiedId'].setValue(clientInfo[0]?.client?.specifiedId ?? '');
    this.allReportForm.controls['subclientSpecifiedId'].setValue(clientInfo[0]?.specifiedId ?? '');
    this.allReportForm.controls['asOf'].setValue(clientInfo[0]?.asOfDate ?? this.initialAsOfDate);
  }

  setAllSubClients(): void {
    this.allReportForm.controls['subclientSpecifiedId'].setValue('ALL_SUB_CLIENTS');
  }

  setAsOfDate(asOfDate: string): void {
    this.allReportForm.controls['asOf'].setValue(asOfDate);
  }

  private scheduleClientSearchAsOfSync(): void {
    this.clearScheduledClientSearchAsOfSync();

    this.clientSearchAsOfSyncRef = afterNextRender(() => {
      this.clientSearchAsOfSyncRef = null;

      const asOfControl = this.clientSearchComponentInstance?.searchForm?.get('asOf');
      if (!asOfControl) {
        return;
      }

      const currentValue = `${asOfControl.value ?? ''}`.trim();
      const normalizedCurrentValue = moment(currentValue, OVERAGE_AS_OF_DATE_FORMATS, true).isValid()
        ? moment(currentValue, OVERAGE_AS_OF_DATE_FORMATS, true).format('YYYY-MM-DD')
        : currentValue;

      if (normalizedCurrentValue === this.clientSearchAsOfDateValue) {
        return;
      }

      asOfControl.setValue(this.clientSearchAsOfDateDisplayValue);
    }, { injector: this.injector });
  }

  private clearScheduledClientSearchAsOfSync(): void {
    this.clientSearchAsOfSyncRef?.destroy();
    this.clientSearchAsOfSyncRef = null;
  }


  searchFormSubmitted(): void {
    if (this.allReportForm.valid) {
      this.isFormSubmitted.set(true);
      this.resetFormData.emit(true);
      this.processFormData.emit({
        ...this.allReportForm.value,
        planAcronym: this.selectedPlanAcronym()
      });
    }
  }

  private normalizePlanAcronym(planAcronym: string | string[] | null | undefined): string {
    if (Array.isArray(planAcronym)) {
      return `${planAcronym[0] ?? ''}`.trim();
    }

    return `${planAcronym ?? ''}`.trim();
  }
}
